import { useCallback, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { ValuationsFormStep, ValuationsFormStepProps } from "../valuations-form-step";
import { ControlledSelect } from "../../../components/forms/controlled-select";
import { SOLICITOR_ALGORITHM_OPTIONS, SolicitorAlgorithm } from "./solicitor-options";
import { useSolicitorLookup } from "./use-solicitor-lookup";
import { SolicitorListItem } from "./solicitor-list-item";
import { InlineTextButton } from "../../../components/buttons/text-button/inline-text-button";
import { Checkbox } from "../../../components/inputs/checkbox";
import { WarningCallout } from "../../../components/alerts/warning-callout";
import { ErrorCallout } from "../../../components/alerts/error-callout";

import { TextLink } from "../../../components/text-link";
import { FontStyles } from "../../../utils/font-styles";
import { CharteredInfoIcon } from "../../../components/chartered-info-icon";
import { RadioGroup } from "../../../components/inputs/radio-group";
import { ExistingSolicitorSearch } from "./existing-solicitor-search";
import { ExistingSolicitorSelected } from "./existing-solicitor-selected";
import { ExistingSolicitorResult } from "./types";
import "../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../assets/fa-icons/css/sharp-regular.css";
import "../../../../assets/fa-icons/css/sharp-solid.css";

export const SolicitorSelectionStep = (props: ValuationsFormStepProps) => {
  // Destructure form context to avoid repeated prop drilling
  const form = props.form.form;
  const { currentStep, previousStep } = props.form;

  // Memoized icon components to prevent unnecessary re-renders
  const SpinnerIcon = useCallback(
    () => <i className="fa-sharp fa-regular fa-spinner-third text-base animate-spin" />,
    [],
  );

  const [postcode, selectedAlgorithm, selectedSolicitors, charteredOnly, hasExistingSolicitor, existingSolicitor] =
    useWatch({
      control: form.control,
      name: [
        "postcode",
        "selectedAlgorithm",
        "selectedSolicitors",
        "charteredOnly",
        "hasExistingSolicitor",
        "existingSolicitor",
      ],
    });

  // Get validation errors
  const solicitorSelectionError = form.formState.errors.selectedSolicitors?.message;
  const existingSolicitorError = form.formState.errors.existingSolicitor?.message;

  const { solicitors, isLoading, error, retry } = useSolicitorLookup(
    postcode,
    selectedAlgorithm as SolicitorAlgorithm | undefined,
    charteredOnly,
  );

  const active = currentStep === props.index;

  // Get the description for the selected algorithm
  const selectedFilterDescription = useMemo(() => {
    if (!selectedAlgorithm) return null;
    const option = SOLICITOR_ALGORITHM_OPTIONS.find((opt) => opt.value === selectedAlgorithm);
    return option?.description ?? null;
  }, [selectedAlgorithm]);

  const handleAlgorithmChange = useCallback(
    (value: string) => {
      form.setValue("selectedAlgorithm", value);
      form.setValue("selectedSolicitors", []);
      // Clear validation error when algorithm changes
      form.clearErrors("selectedSolicitors");
    },
    [form],
  );

  const handleSolicitorToggle = useCallback(
    (firmId: number, organisationId: number | null) => {
      const current = selectedSolicitors;
      // Check combination of firmId + organisationId for unique identification
      const isSelected = current.some((s) => s.firmId === firmId && s.organisationId === organisationId);

      if (isSelected) {
        // Remove by matching BOTH firmId and organisationId
        const updated = current.filter((s) => !(s.firmId === firmId && s.organisationId === organisationId));
        form.setValue("selectedSolicitors", updated);
      } else {
        // Add full object with both IDs
        const updated = [...current, { firmId, organisationId }];
        form.setValue("selectedSolicitors", updated);
      }

      // Clear validation error when user interacts with solicitor selection
      form.clearErrors("selectedSolicitors");
    },
    [selectedSolicitors, form],
  );

  const handleHasExistingSolicitorChange = useCallback(
    (value: string) => {
      const newValue = value as "yes" | "no";
      form.setValue("hasExistingSolicitor", newValue);
      form.clearErrors("hasExistingSolicitor");

      // Reset selections when toggling
      if (newValue === "yes") {
        // Clear algorithm-based selections
        form.setValue("selectedSolicitors", []);
        form.setValue("selectedAlgorithm", "ActiveNearby");
        form.setValue("charteredOnly", false);
        form.clearErrors("selectedSolicitors");
      } else {
        // Clear existing solicitor selection
        form.setValue("existingSolicitor", undefined);
        form.clearErrors("existingSolicitor");
      }
    },
    [form],
  );

  const handleExistingSolicitorSelect = useCallback(
    (solicitor: ExistingSolicitorResult) => {
      form.setValue("existingSolicitor", solicitor);
      form.clearErrors("existingSolicitor");
    },
    [form],
  );

  const handleExistingSolicitorReset = useCallback(() => {
    form.setValue("existingSolicitor", undefined);
  }, [form]);

  const getSummaryText = useCallback((): string | string[] => {
    // If step is completed, show what was selected
    if (!active) {
      // If user selected existing solicitor
      if (hasExistingSolicitor === "yes" && existingSolicitor) {
        return existingSolicitor.name;
      }

      // If user selected from algorithm suggestions
      if (hasExistingSolicitor === "no" && selectedSolicitors.length > 0) {
        const selectedCount = selectedSolicitors.length;
        const selectedNames = solicitors
          .filter((s) =>
            selectedSolicitors.some((sel) => sel.firmId === s.firmId && sel.organisationId === s.organisationId),
          )
          .map((s) => s.name)
          .slice(0, 3);

        if (selectedCount === 1) {
          return selectedNames[0];
        } else if (selectedCount <= 3) {
          return selectedNames.join(", ");
        } else {
          return `${selectedNames.slice(0, 2).join(", ")} and ${(selectedCount - 2).toString()} more`;
        }
      }
    }

    return "Select the solicitor estate agents that you would like to receive a valuation from";
  }, [active, hasExistingSolicitor, existingSolicitor, selectedSolicitors, solicitors]);

  return (
    <ValuationsFormStep {...props} text={getSummaryText()}>
      {active && (
        <div className="w-full flex flex-col gap-4">
          {/* Radio question: Do you have an existing solicitor? */}
          <RadioGroup
            value={hasExistingSolicitor ?? ""}
            onValueChange={handleHasExistingSolicitorChange}
            label="Do you already have an existing solicitor?"
            showRequiredAsterisk={true}
            state={form.formState.errors.hasExistingSolicitor ? "error" : "default"}
            message={form.formState.errors.hasExistingSolicitor?.message}
            layout="horizontal"
            options={[
              { value: "yes", label: "Yes", id: "has-existing-solicitor-yes" },
              { value: "no", label: "No", id: "has-existing-solicitor-no" },
            ]}
          />

          {/* Show existing solicitor search when Yes is selected */}
          {hasExistingSolicitor === "yes" && (
            <>
              {!existingSolicitor ? (
                <ExistingSolicitorSearch onSelect={handleExistingSolicitorSelect} error={existingSolicitorError} />
              ) : (
                <ExistingSolicitorSelected
                  solicitorName={existingSolicitor.name}
                  onReset={handleExistingSolicitorReset}
                />
              )}
            </>
          )}

          {/* Show algorithm selection when No is selected */}
          {hasExistingSolicitor === "no" && (
            <>
              {/* Filter controls and description - uses flexbox order for mobile/desktop layouts */}
              <div className="w-full flex flex-col md:flex-row md:flex-wrap md:justify-between gap-4">
                {/* Filter dropdown - order-1 on mobile, order-0 on desktop */}
                <div className="w-full md:w-[340px] order-1 md:order-0">
                  <ControlledSelect
                    control={form.control}
                    name="selectedAlgorithm"
                    label="Filter solicitors by..."
                    options={SOLICITOR_ALGORITHM_OPTIONS.map((opt) => ({
                      name: opt.name,
                      value: opt.value,
                    }))}
                    onValueChange={handleAlgorithmChange}
                    renderTrailingIcon={isLoading ? SpinnerIcon : undefined}
                  />
                </div>

                {/* Filter description - order-2 on both mobile and desktop, but wraps to new line on desktop */}
                {selectedFilterDescription && (
                  <p
                    className={`w-full order-2 ${FontStyles["body-md"]} text-light-slate leading-snug md:leading-snug`}
                  >
                    {selectedFilterDescription}
                  </p>
                )}

                {/* Chartered checkbox - order-3 on mobile, order-1 on desktop (after dropdown, before description) */}
                <div className="order-3 md:order-1 flex items-center justify-center md:justify-end gap-1.5 md:pb-3.5">
                  <Checkbox
                    id="chartered-only-checkbox"
                    checked={charteredOnly}
                    onChange={(checked) => {
                      form.setValue("charteredOnly", checked);
                      form.setValue("selectedSolicitors", []);
                      // Clear validation error when filter changes
                      form.clearErrors("selectedSolicitors");
                    }}
                    label="Only show Chartered firms?"
                  />
                  <CharteredInfoIcon />
                </div>
              </div>

              {/* Empty state - no solicitors found */}
              {selectedAlgorithm && solicitors.length === 0 && !isLoading && !error && (
                <WarningCallout style="outlined">
                  <i className={`fa-sharp fa-regular fa-circle-exclamation mr-2 ${FontStyles["body-s"]}`} />
                  Sorry, we couldn't find any Solicitor Estate Agents covering your area. If you think this is a
                  mistake, <InlineTextButton onClick={() => previousStep()}>go back</InlineTextButton> and check your
                  address in the previous step.
                </WarningCallout>
              )}

              {/* Error state - API request failed */}
              {error && (
                <ErrorCallout style="outlined">
                  <i className={`fa-sharp fa-regular fa-circle-exclamation mr-2 ${FontStyles["body-s"]}`} />
                  Sorry, something went wrong while processing your request. Please{" "}
                  <InlineTextButton onClick={retry}>try submitting the form again</InlineTextButton>
                  {", "}
                  or <TextLink href="mailto:support@espc.com">contact our support team</TextLink> for assistance.
                </ErrorCallout>
              )}

              {/* Solicitor list */}
              {!error && (solicitors.length > 0 || isLoading) && (
                <div className="w-full relative isolate">
                  {/* Solicitor list - dimmed when loading */}
                  <div
                    className={`w-full flex flex-col gap-4 relative ${isLoading ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    {solicitors.map((solicitor) => (
                      <SolicitorListItem
                        key={`${solicitor.firmId.toString()}-${solicitor.organisationId?.toString() ?? "null"}`}
                        solicitor={solicitor}
                        isSelected={selectedSolicitors.some(
                          (s) => s.firmId === solicitor.firmId && s.organisationId === solicitor.organisationId,
                        )}
                        onToggle={() => handleSolicitorToggle(solicitor.firmId, solicitor.organisationId)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Validation error - no solicitor selected */}
              {solicitorSelectionError && (
                <WarningCallout style="outlined">
                  <i className={`fa-sharp fa-regular fa-circle-exclamation mr-2 ${FontStyles["body-s"]}`} />
                  {solicitorSelectionError}
                </WarningCallout>
              )}
            </>
          )}
        </div>
      )}
    </ValuationsFormStep>
  );
};
